/**
 * `schemas/playback.ts` — resume state and play events, `spec.md` §6.6.
 *
 * The server is the source of truth for "where was I", so that a resume works across
 * devices. Two details in here exist to stop that being lossy:
 *
 *  - `PUT /api/playback` is a FULL REPLACE with last-write-wins on `updated_at`. A body
 *    whose implied `updatedAt` is older than the stored row is accepted and IGNORED
 *    (`200 { applied: false }`) so a stale minimised tab cannot rewind a live one. The
 *    response therefore has to carry `applied`, and a client must read it.
 *  - `POST /api/plays` is ignored below 5 s of playback: a skip is not a play.
 */
import * as z from 'zod';
import { Ms, TrackId, Uint32, UnixSeconds } from './common.js';
import { TrackSummarySchema } from './track.js';
/**
 * `playback_state.repeat_mode`, in the DDL's own CHECK order: `off | all | one`.
 *
 * `constants.ts` (T10) exports the same set as `REPEAT_MODES` for the player. The two
 * names are deliberately distinct so the package barrel can re-export both without an
 * ambiguous export; whoever touches either must keep the member lists identical.
 */
export const REPEAT_MODE_VALUES = ['off', 'all', 'one'];
export const RepeatMode = z.enum(REPEAT_MODE_VALUES);
/** `playback_state.volume` is a `REAL` in 0..1. */
export const Volume = z.number().min(0).max(1);
/** The queue index the state points at; `-1`/`0` on an empty queue. */
export const PlaybackQueueIndex = z.int().min(-1);
/**
 * The `playback_state` row as stored (`spec.md` §5.2), camel-cased. `PUT` sends exactly
 * these fields minus `updatedAt`, which the server stamps.
 */
export const PlaybackStateSchema = z.strictObject({
    trackId: TrackId.nullable(),
    positionMs: Ms,
    queueIndex: PlaybackQueueIndex,
    shuffle: z.boolean(),
    /** Deterministic Fisher-Yates seed, so a shuffled order survives a reload (§13.9). */
    shuffleSeed: Uint32,
    repeatMode: RepeatMode,
    volume: Volume,
    updatedAt: UnixSeconds,
});
/**
 * `GET /api/playback` — the row plus the hydrated track, so the resume banner can paint
 * without a second request. `track` is `null` when `trackId` is null or the track has
 * since been soft-deleted.
 */
export const PlaybackResponseSchema = PlaybackStateSchema.extend({
    track: TrackSummarySchema.nullable(),
});
/** `PUT /api/playback` — full replace. `updatedAt` is the server's to set. */
export const PlaybackPutRequestSchema = PlaybackStateSchema.omit({ updatedAt: true });
/** `applied: false` means a newer state was already stored; the client must re-read. */
export const PlaybackPutResponseSchema = z.strictObject({
    applied: z.boolean(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/plays
 * ──────────────────────────────────────────────────────────────────────────── */
/** `plays.source` — the closed set from the DDL comment in `spec.md` §5.2. */
export const PLAY_SOURCES = [
    'player',
    'queue',
    'playlist',
    'search',
    'shuffle',
    'deeplink',
    'inline',
];
export const PlaySource = z.enum(PLAY_SOURCES);
/** Below this, `POST /api/plays` returns `{ counted: false }`: a skip is not a play. */
export const PLAY_MIN_COUNTED_MS = 5000;
export const PlayCreateRequestSchema = z.strictObject({
    trackId: TrackId,
    msPlayed: Ms,
    completed: z.boolean(),
    source: PlaySource,
});
/**
 * Deliberately just `{ counted }`. §6.6 specifies `200 { counted: false }` for the
 * ignored case and says nothing else about the body, and a richer invented shape here
 * would be a contract nobody asked for.
 */
export const PlayCreateResponseSchema = z.strictObject({
    counted: z.boolean(),
});
/** The `plays` row, for the query layer and the history join. */
export const PlayRowSchema = z.strictObject({
    id: z.int().positive(),
    userId: z.int().positive(),
    trackId: TrackId,
    startedAt: UnixSeconds,
    msPlayed: Ms,
    completed: z.boolean(),
    source: PlaySource,
});
//# sourceMappingURL=playback.js.map